import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { ArrowRight, BarChart3, TrendingUp, Building2, Shield, Globe, Sparkles, Check, Users, Briefcase, Building, ChevronDown } from "lucide-react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { api } from "../context/AuthContext";
import TrueFlowLogo from "../components/TrueFlowLogo";
import { getPlanFeatures } from "../lib/planFeatures";
import { planPrice } from "../lib/planPricing";

const LANGUAGES = [
  { code: "es", name: "Español" },
  { code: "en", name: "English" },
  { code: "pt", name: "Português" },
  { code: "de", name: "Deutsch" },
  { code: "fr", name: "Français" },
  { code: "it", name: "Italiano" },
];

const PLAN_ICONS = {
  entry:    <Briefcase className="w-6 h-6" />,
  standard: <Building2 className="w-6 h-6" />,
  gold:     <Building className="w-6 h-6" />,
};


// El formato vive en `lib/planPricing.js`, compartido con /subscription. Estaban
// formateando distinto —acá con Intl, allá con un "$" tecleado— y al agregar el IVA
// habría quedado un tercer formato.
//
// La landing muestra el precio NETO: no sabe quién está mirando, así que no puede
// saber si corresponde IVA. El impuesto se calcula al contratar, cuando el cliente ya
// declaró su país. La nota debajo de las tarjetas lo dice.
function formatPrice(plan, yearly, language, t) {
  if (plan.price_monthly === 0) return t("landing.plans.priceLabels.free");
  return planPrice(plan, yearly, language, null).formatted;
}

/* ── Hero visual: large brand icon + floating KPI cards ── */
function BrandHeroVisual() {
  const { t } = useTranslation();
  return (
    <div className="relative flex items-center justify-center h-[420px] lg:h-[480px]">
      {/* ambient glow */}
      <div className="absolute inset-0 rounded-3xl"
           style={{ background: "radial-gradient(ellipse 70% 60% at 55% 45%, rgba(26,58,108,0.07) 0%, transparent 80%)" }} />

      {/* large brand icon — stroke widths tuned for 300px render */}
      <svg width="300" height="263" viewBox="0 0 96 84" xmlns="http://www.w3.org/2000/svg">
        <g transform="translate(4,6)">
          <path d="M4,52 C24,52 52,18 82,10 L82,24 C52,32 24,66 4,66 Z"
                fill="#1A3A6C" opacity="0.06"/>
          <path d="M4,52 C24,52 52,18 82,10"
                fill="none" stroke="#1A3A6C" strokeWidth="0.88" strokeLinecap="round"/>
          <path d="M4,66 C24,66 52,32 82,24"
                fill="none" stroke="#1A3A6C" strokeWidth="0.55" strokeLinecap="round" opacity="0.38"/>
          <circle cx="82" cy="10" r="11" fill="#C49040" opacity="0.12"/>
          <circle cx="82" cy="10" r="7"  fill="#C49040" opacity="0.28"/>
          <circle cx="82" cy="10" r="4"  fill="#C49040"/>
          <circle cx="82" cy="24" r="3.5" fill="#1A3A6C" opacity="0.28"/>
        </g>
      </svg>

      {/* KPI card — top right */}
      <motion.div
        initial={{ opacity: 0, y: -12, x: 12 }}
        animate={{ opacity: 1, y: 0, x: 0 }}
        transition={{ delay: 0.6, duration: 0.5 }}
        className="absolute top-10 right-0 bg-white rounded-2xl shadow-lg border border-border p-4 min-w-[152px]"
      >
        <p className="text-[10px] font-bold tracking-widest uppercase text-muted-foreground mb-1">{t("landing.hero.kpi.netFlow")}</p>
        <p className="text-2xl font-extrabold tracking-tight" style={{ color: "#1A3A6C" }}>$3.84M</p>
        <p className="text-xs font-semibold text-emerald-600 mt-0.5 flex items-center gap-1">
          <TrendingUp className="w-3 h-3" /> +12.4% {t("landing.hero.kpi.vsPrev")}
        </p>
      </motion.div>

      {/* KPI card — bottom left */}
      <motion.div
        initial={{ opacity: 0, y: 12, x: -12 }}
        animate={{ opacity: 1, y: 0, x: 0 }}
        transition={{ delay: 0.8, duration: 0.5 }}
        className="absolute bottom-10 left-0 bg-white rounded-2xl shadow-lg border border-border p-4 min-w-[160px]"
      >
        <p className="text-[10px] font-bold tracking-widest uppercase text-muted-foreground mb-1">{t("landing.hero.kpi.projection")}</p>
        <p className="text-xl font-extrabold tracking-tight" style={{ color: "#C49040" }}>$4.2M</p>
        <p className="text-xs text-muted-foreground mt-0.5">{t("landing.hero.kpi.basedOnHistory")}</p>
      </motion.div>

    </div>
  );
}

export default function Landing() {
  const { t, i18n } = useTranslation();
  const currentLang = LANGUAGES.find(l => l.code === i18n.language) || LANGUAGES[0];
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [billingYearly, setBillingYearly] = useState(false);

  useEffect(() => {
    api.get("/public/plans")
      .then(res => setPlans(res.data))
      .catch(() => setPlans([]))
      .finally(() => setPlansLoading(false));
  }, []);

  const features = [
    { icon: <Building2 className="w-6 h-6" strokeWidth={1.5} />, key: "flexible" },
    { icon: <BarChart3 className="w-6 h-6" strokeWidth={1.5} />,  key: "flow"     },
    { icon: <TrendingUp className="w-6 h-6" strokeWidth={1.5} />, key: "ai"       },
    { icon: <Shield className="w-6 h-6" strokeWidth={1.5} />,     key: "security" },
    { icon: <Globe className="w-6 h-6" strokeWidth={1.5} />,      key: "multi"    },
    { icon: <Sparkles className="w-6 h-6" strokeWidth={1.5} />,   key: "alerts"   },
  ];

  const useCases = [
    {
      key: "entrepreneurs",
      icon: <Briefcase className="w-6 h-6" />,
      cardStyle: { background: "linear-gradient(135deg, rgba(26,58,108,0.04) 0%, white 100%)", border: "1px solid rgba(26,58,108,0.10)" },
      iconStyle: { background: "rgba(26,58,108,0.08)", color: "#1A3A6C" },
    },
    {
      key: "sme",
      icon: <Users className="w-6 h-6" />,
      cardStyle: { background: "linear-gradient(135deg, rgba(196,144,64,0.06) 0%, white 100%)", border: "1px solid rgba(196,144,64,0.12)" },
      iconStyle: { background: "rgba(196,144,64,0.10)", color: "#9A7030" },
    },
    {
      key: "holdings",
      icon: <Building className="w-6 h-6" />,
      cardStyle: { background: "linear-gradient(135deg, rgba(26,58,108,0.04) 0%, white 100%)", border: "1px solid rgba(26,58,108,0.10)" },
      iconStyle: { background: "rgba(26,58,108,0.06)", color: "#1A3A6C" },
    },
  ];

  return (
    <div className="min-h-screen bg-white">

      {/* ── Header ── */}
      <header className="glass-header sticky top-0 z-50 border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <TrueFlowLogo variant="light" />
          <div className="flex items-center gap-4">
            <a href="#precios" className="hidden sm:block text-sm text-muted-foreground hover:text-foreground transition-colors">
              {t("landing.nav.pricing")}
            </a>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-1" data-testid="language-selector">
                  <Globe className="w-4 h-4" />
                  <span className="hidden sm:inline">{currentLang.name}</span>
                  <ChevronDown className="w-3 h-3 opacity-60" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-40">
                {LANGUAGES.map(lang => (
                  <DropdownMenuItem
                    key={lang.code}
                    onClick={() => i18n.changeLanguage(lang.code)}
                    className={i18n.language === lang.code ? "bg-primary/10" : ""}
                  >
                    {lang.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <Link to="/login">
              <Button variant="ghost" data-testid="login-nav-btn">{t("auth.login")}</Button>
            </Link>
            <Link to="/register">
              <Button data-testid="register-nav-btn">{t("auth.register")}</Button>
            </Link>
          </div>
        </div>
      </header>

      {/* ── Hero ── */}
      <section className="max-w-7xl mx-auto px-6 py-16 lg:py-24">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">

          {/* Text column */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-1.5 rounded-full text-sm font-semibold mb-6">
              <Sparkles className="w-4 h-4" />
              {t("landing.hero.badge")}
            </div>
            <h1 className="font-heading font-bold text-4xl sm:text-5xl text-foreground leading-[1.08] tracking-tight">
              {t("landing.hero.titleBefore") && <>{t("landing.hero.titleBefore")}<br /></>}
              <span className="text-primary">{t("landing.hero.titleHighlight")}</span>
              {t("landing.hero.titleAfter") && <><br />{t("landing.hero.titleAfter")}</>}
            </h1>
            <p className="mt-6 text-lg text-muted-foreground leading-relaxed max-w-xl">
              {t("landing.hero.description")}
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-3">
              <Link to="/register">
                <Button size="lg" className="w-full sm:w-auto px-8" data-testid="hero-cta-btn">
                  {t("landing.hero.cta")}
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
              <a href="#precios">
                <Button size="lg" variant="ghost" className="w-full sm:w-auto px-8" data-testid="hero-pricing-btn">
                  {t("landing.hero.viewPlans")}
                </Button>
              </a>
            </div>
            <p className="mt-4 text-sm text-muted-foreground">{t("landing.hero.trial")}</p>
          </motion.div>

          {/* Visual column */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="hidden lg:block"
          >
            <BrandHeroVisual />
          </motion.div>
        </div>
      </section>

      {/* ── Social Proof ── */}
      <section className="border-y border-border py-8" style={{ background: "rgba(26,58,108,0.02)" }}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-wrap items-center justify-center gap-10 text-center">
            {[
              { value: "500+",  label: t("landing.stats.companies") },
              { value: "$2B+",  label: t("landing.stats.flow")      },
              { value: "15",    label: t("landing.stats.countries")  },
              { value: "99.9%", label: t("landing.stats.uptime")     },
            ].map(({ value, label }, i) => (
              <div key={i} className="flex items-center gap-10">
                {i > 0 && <div className="w-px h-10 bg-border hidden sm:block" />}
                <div>
                  <p className="text-3xl font-extrabold tracking-tight" style={{ color: "#1A3A6C" }}>{value}</p>
                  <p className="text-sm text-muted-foreground mt-0.5">{label}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Features ── */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-foreground tracking-tight">
              {t("landing.features.title")}
            </h2>
            <p className="mt-4 text-muted-foreground max-w-2xl mx-auto">
              {t("landing.features.subtitle")}
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {features.map((feature, index) => (
              <motion.div
                key={feature.key}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.08 }}
                viewport={{ once: true }}
                className="bg-white p-6 rounded-xl border border-border hover:shadow-md transition-shadow"
                data-testid={`feature-card-${index}`}
              >
                <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center text-primary mb-4">
                  {feature.icon}
                </div>
                <h3 className="font-heading font-semibold text-base text-foreground mb-2">
                  {t(`landing.features.items.${feature.key}.title`)}
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {t(`landing.features.items.${feature.key}.desc`)}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Pricing ── */}
      <section id="precios" className="py-20" style={{ background: "rgba(26,58,108,0.02)" }}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-foreground tracking-tight">
              {t("landing.plans.title")}
            </h2>
            <p className="mt-4 text-muted-foreground max-w-2xl mx-auto">
              {t("landing.plans.subtitle")}
            </p>

            {/* billing toggle */}
            <div className="mt-8 inline-flex items-center gap-3 bg-white border border-border rounded-full px-4 py-2 shadow-sm">
              <span className={`text-sm font-medium transition-colors ${!billingYearly ? "text-foreground" : "text-muted-foreground"}`}>
                {t("landing.plans.monthly")}
              </span>
              <button
                onClick={() => setBillingYearly(v => !v)}
                className={`relative w-11 h-6 rounded-full transition-colors focus:outline-none ${billingYearly ? "bg-primary" : "bg-muted"}`}
                role="switch"
                aria-checked={billingYearly}
              >
                <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${billingYearly ? "translate-x-5" : "translate-x-0"}`} />
              </button>
              <span className={`text-sm font-medium transition-colors ${billingYearly ? "text-foreground" : "text-muted-foreground"}`}>
                {t("landing.plans.yearly")}
              </span>
              {billingYearly && (
                <span className="text-xs font-bold px-2 py-0.5 rounded-full"
                      style={{ background: "rgba(196,144,64,0.12)", color: "#9A7030" }}>
                  {t("landing.plans.savings")}
                </span>
              )}
            </div>
          </div>

          {plansLoading ? (
            <div className="grid md:grid-cols-3 gap-6 lg:gap-8 max-w-6xl mx-auto">
              {[0, 1, 2].map(i => (
                <div key={i} className="bg-white rounded-xl border-2 border-border p-6 lg:p-8 animate-pulse">
                  <div className="h-10 w-10 rounded-lg bg-muted mb-4" />
                  <div className="h-5 w-24 bg-muted rounded mb-2" />
                  <div className="h-3 w-36 bg-muted rounded mb-6" />
                  <div className="h-10 w-20 bg-muted rounded mb-6" />
                  {[0,1,2,3,4].map(j => <div key={j} className="h-3 bg-muted rounded mb-3" />)}
                </div>
              ))}
            </div>
          ) : (
            <div className="grid gap-6 lg:gap-8 max-w-6xl mx-auto md:grid-cols-3">
              {plans.map((plan, index) => {
                const ctaVariant = plan.popular ? "default" : "outline";
                const hasTrial = (plan.trial_days ?? 0) > 0;
                // Con trial → CTA de "empezar gratis"; sin trial → "Elegir {nombre}"
                // (usa plan.name, no el código, para no mostrar el nombre interno).
                const ctaText = hasTrial
                  ? t("landing.plans.cta.trial")
                  : t("landing.plans.cta.choose", { name: plan.name });
                const icon = PLAN_ICONS[plan.code] ?? <Sparkles className="w-6 h-6" />;
                const priceLabel = formatPrice(plan, billingYearly, i18n.language, t);
                const planFeatures = getPlanFeatures(plan, t);

                return (
                  <motion.div
                    key={plan.code}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.15 }}
                    viewport={{ once: true }}
                    className={`relative bg-white rounded-xl border-2 p-6 lg:p-8 flex flex-col ${
                      plan.popular ? "border-primary shadow-lg scale-105" : "border-border"
                    }`}
                    data-testid={`pricing-card-${plan.code}`}
                  >
                    {plan.popular && (
                      <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 text-white px-3"
                             style={{ background: "#C49040", borderColor: "#C49040" }}>
                        {t("landing.plans.popular")}
                      </Badge>
                    )}
                    <div className="flex items-center gap-3 mb-4">
                      <div className={`w-10 h-10 rounded-lg ${plan.color} flex items-center justify-center text-white`}>
                        {icon}
                      </div>
                      <div>
                        <h3 className="font-heading font-semibold text-xl text-foreground">{plan.name}</h3>
                        <p className="text-xs text-muted-foreground">{t(`landing.plans.descriptions.${plan.code}`, plan.description)}</p>
                      </div>
                    </div>
                    <div className="mb-6">
                      <div className="flex items-baseline gap-1">
                        <span className="text-4xl font-bold text-foreground">{priceLabel}</span>
                        {plan.price_monthly > 0 && (
                          <>
                            <span className="text-muted-foreground">{plan.currency}</span>
                            <span className="text-muted-foreground">{billingYearly ? t("landing.plans.perYear") : t("landing.plans.perMonth")}</span>
                          </>
                        )}
                      </div>
                      {hasTrial && (
                        <p className="text-xs font-semibold text-emerald-600 mt-1">
                          {t("landing.plans.trialDays", { days: plan.trial_days })}
                        </p>
                      )}
                    </div>
                    <ul className="space-y-3 mb-8 flex-1">
                      {planFeatures.map((feature, fIndex) => (
                        <li key={fIndex} className="flex items-start gap-2 text-sm">
                          <Check className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                          <span className="text-muted-foreground">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <Link to="/register" className="mt-auto">
                      <Button variant={ctaVariant} className="w-full" data-testid={`pricing-cta-${plan.code}`}>
                        {ctaText}
                      </Button>
                    </Link>
                  </motion.div>
                );
              })}
            </div>
          )}

          {/* Nota de impuestos, neutra a propósito. La landing no sabe de qué país es
              quien mira, así que no puede anunciar ni "IVA incluido" ni "sin IVA" sin
              inventar. Se dice qué determina el impuesto y dónde se calcula. */}
          <p className="text-center text-xs text-muted-foreground mt-8">
            {t("landing.plans.taxNote")}
          </p>

        </div>
      </section>

      {/* ── Use Cases ── */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-foreground tracking-tight">
              {t("landing.useCases.title")}
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {useCases.map(({ key, icon, cardStyle, iconStyle }, index) => (
              <motion.div
                key={key}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="p-7 rounded-xl"
                style={cardStyle}
              >
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-5" style={iconStyle}>
                  {icon}
                </div>
                <h3 className="font-heading font-bold text-lg mb-2">
                  {t(`landing.useCases.${key}.title`)}
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                  {t(`landing.useCases.${key}.quote`)}
                </p>
                <p className="text-xs font-medium text-muted-foreground">
                  {t(`landing.useCases.${key}.author`)}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA Section ── */}
      <section className="py-20" style={{ background: "#1A3A6C" }}>
        <div className="max-w-4xl mx-auto px-6 text-center">
          {/* brand icon small in the CTA */}
          <div className="flex justify-center mb-8">
            <svg width="56" height="49" viewBox="0 0 96 84" xmlns="http://www.w3.org/2000/svg">
              <g transform="translate(4,6)">
                <path d="M4,52 C24,52 52,18 82,10 L82,24 C52,32 24,66 4,66 Z" fill="#60A5FA" opacity="0.08"/>
                <path d="M4,52 C24,52 52,18 82,10" fill="none" stroke="#60A5FA" strokeWidth="3" strokeLinecap="round"/>
                <path d="M4,66 C24,66 52,32 82,24" fill="none" stroke="#60A5FA" strokeWidth="2" strokeLinecap="round" opacity="0.35"/>
                <circle cx="82" cy="10" r="11" fill="#F0C860" opacity="0.16"/>
                <circle cx="82" cy="10" r="7"  fill="#F0C860" opacity="0.34"/>
                <circle cx="82" cy="10" r="4"  fill="#F0C860"/>
              </g>
            </svg>
          </div>
          <h2 className="font-heading font-bold text-3xl sm:text-4xl text-white mb-4 tracking-tight">
            {t("landing.ctaSection.title")}
          </h2>
          <p className="mb-8 max-w-xl mx-auto leading-relaxed" style={{ color: "rgba(255,255,255,0.65)" }}>
            {t("landing.ctaSection.subtitle")}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register">
              <Button size="lg" variant="secondary" data-testid="cta-register-btn">
                {t("landing.ctaSection.button")}
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
            <a href="#precios">
              <Button size="lg" variant="outline"
                      className="border-white/25 text-white hover:bg-white/10">
                {t("landing.ctaSection.comparePlans")}
              </Button>
            </a>
          </div>
          <p className="mt-4 text-sm" style={{ color: "rgba(255,255,255,0.4)" }}>
            {t("landing.ctaSection.noCard")}
          </p>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="bg-foreground py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <TrueFlowLogo variant="dark" className="mb-4" />
              <p className="text-sm text-muted-foreground">{t("landing.footer.tagline")}</p>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-3">{t("landing.footer.product")}</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><a href="#precios" className="hover:text-white transition-colors">{t("landing.footer.pricing")}</a></li>
                <li><Link to="/register" className="hover:text-white transition-colors">{t("landing.footer.register")}</Link></li>
                <li><Link to="/login" className="hover:text-white transition-colors">{t("landing.footer.login")}</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-3">{t("landing.footer.resources")}</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><a href="#" className="hover:text-white transition-colors">{t("landing.footer.docs")}</a></li>
                <li><a href="#" className="hover:text-white transition-colors">{t("landing.footer.blog")}</a></li>
                <li><a href="#" className="hover:text-white transition-colors">{t("landing.footer.helpCenter")}</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-3">{t("landing.footer.contact")}</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><a href="mailto:contact@trueflow.cash" className="hover:text-white transition-colors">contact@trueflow.cash</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-muted-foreground text-sm">{t("landing.footer.copyright")}</p>
            <div className="flex gap-6 text-sm text-muted-foreground">
              <a href="#" className="hover:text-white transition-colors">{t("landing.footer.terms")}</a>
              <a href="#" className="hover:text-white transition-colors">{t("landing.footer.privacy")}</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
